import React from 'react';
import { Drawer, Typography, IconButton, Grid, Button, Box, Divider } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';
import { customtheme } from '../../../app/theme/theme';

const useStyles = makeStyles(() => ({
  drawer: { width: 560, maxWidth: '100vw' },
  drawerHeader: {
    padding: 16,
    borderBottom: '1px solid #ddd',
    background: '#f8f9fb',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  drawerContent: {
    background: '#f3f5f8',
    overflowY: 'auto',
    padding: 16,
    height: '100%',
  },
  drawerFooter: {
    padding: 16,
    borderTop: '1px solid #eee',
    background: customtheme.gray[200],
    display: 'flex',
    justifyContent: 'flex-end',
  },
  drawerSummary: {
    background: '#ffffff',
    border: '1px solid #dfe5ec',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  },
  summaryLabel: {
    color: '#64748b',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  summaryValue: {
    fontWeight: 700,
    color: '#111827',
  },
  itemCard: {
    background: '#ffffff',
    border: '1px solid #dfe5ec',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
  },
  itemTitle: {
    fontWeight: 700,
    color: '#111827',
    marginBottom: 8,
  },
  itemMeta: {
    color: '#475569',
  },
}));

interface Props {
  open: boolean;
  onClose: () => void;
  record?: any | null;
  onApprove: () => void;
  isApproving?: boolean;
}

const ApproveQuotesEmitDrawer: React.FC<Props> = ({ open, onClose, record, onApprove, isApproving }) => {
  const classes = useStyles();

  const formatCurrency = (value: any) => {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return value ?? '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(numericValue);
  };

  const formatDate = (value: any) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('pt-BR');
  };

  const readFirstValue = (source: any, keys: string[]) => {
    for (const key of keys) {
      const value = source?.[key];
      if (value !== null && value !== undefined && value !== '') return value;
    }
    return '';
  };

  const normalizeToNumber = (value: any) => {
    const rawValue = String(value ?? '').trim();
    if (!rawValue) return 0;
    const normalized = rawValue.includes(',') ? rawValue.replace(/\./g, '').replace(',', '.') : rawValue;
    const numericValue = Number(normalized);
    return Number.isNaN(numericValue) ? 0 : numericValue;
  };

  const solicitation = record?.solicitacao ?? record ?? {};
  const quote = record?.cotacao ?? record ?? {};
  const quantity = readFirstValue(solicitation, ['qtdsol', 'qtd', 'quantidade']);
  const unitPrice = readFirstValue(quote, ['precot', 'valorunitario', 'vlruni', 'precounitario']);
  const netValue = normalizeToNumber(quantity) * normalizeToNumber(unitPrice);
  const summaryTotal = netValue || normalizeToNumber(readFirstValue(quote, ['vlrcot', 'valorliquido', 'vlrlpr']));

  return (
    <Drawer anchor="right" open={open} onClose={onClose} classes={{ paper: classes.drawer }}>
      <Box display="flex" flexDirection="column" height="100%">
        <Box className={classes.drawerHeader}>
          <Box>
            <Typography variant="h6">Cotação</Typography>
            <Typography variant="body2" color="textSecondary">
              {quote?.numcot ? `Nº ${quote.numcot}` : 'Detalhes da cotação'}
            </Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon style={{ color: customtheme.gray[400] }} />
          </IconButton>
        </Box>

        <Box className={classes.drawerContent}>
          <Box className={classes.drawerSummary}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography className={classes.summaryLabel}>Fornecedor</Typography>
                <Typography className={classes.summaryValue}>{quote?.apefor || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography className={classes.summaryLabel}>Produto</Typography>
                <Typography className={classes.summaryValue}>{quote?.cplite || solicitation?.cplpro || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography className={classes.summaryLabel}>Nº Solicitação</Typography>
                <Typography className={classes.summaryValue}>{solicitation?.numsol ?? record?.numsol ?? '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography className={classes.summaryLabel}>Data Cotação</Typography>
                <Typography className={classes.summaryValue}>{formatDate(quote?.datcot)}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography className={classes.summaryLabel}>Valor Líquido</Typography>
                <Typography className={classes.summaryValue}>{formatCurrency(summaryTotal)}</Typography>
              </Grid>
            </Grid>
          </Box>

          <Box mb={1} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Solicitação</Typography>
          </Box>
          <Divider style={{ marginBottom: 16 }} />

          <Box className={classes.itemCard}>
            <Typography variant="subtitle1" className={classes.itemTitle}>Dados da Solicitação</Typography>
            <Typography variant="body2" className={classes.itemMeta}>Empresa: {solicitation?.nomemp || '-'}</Typography>
            <Typography variant="body2" className={classes.itemMeta}>Filial: {solicitation?.nomfil || '-'}</Typography>
            <Typography variant="body2" className={classes.itemMeta}>Solicitante: {solicitation?.nomususol || '-'}</Typography>
            <Typography variant="body2" className={classes.itemMeta}>Quantidade: {quantity || '-'}</Typography>
            <Typography variant="body2" className={classes.itemMeta}>Unidade: {solicitation?.unimed || '-'}</Typography>
          </Box>

          <Box mb={1} mt={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Itens da Cotação</Typography>
          </Box>
          <Divider style={{ marginBottom: 16 }} />

          <Box className={classes.itemCard}>
            <Typography variant="subtitle1" className={classes.itemTitle}>{quote?.cplite || solicitation?.cplpro || 'Item sem descrição'}</Typography>
            <Typography variant="body2" className={classes.itemMeta}>Preço Unitário: {formatCurrency(unitPrice)}</Typography>
            <Typography variant="body2" className={classes.itemMeta}>Quantidade: {quantity || '-'}</Typography>
            <Typography variant="body2" className={classes.itemMeta}>Valor Líquido: {formatCurrency(netValue)}</Typography>
          </Box>
        </Box>

        <Box className={classes.drawerFooter}>
          <Button onClick={onClose} style={{ marginRight: 8 }}>Voltar</Button>
          <Button variant="contained" color="primary" onClick={onApprove} disabled={isApproving}>{isApproving ? 'Aprovando...' : 'Aprovar'}</Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ApproveQuotesEmitDrawer;
